/**
 * ys142857 我的好友 / 附近玩家 — direct referrals with L/R leg stats.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { binaryTreeAPI, type FriendRow } from '../api/binary-tree';
import { EmptyState } from '../components/ui/EmptyState';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import {
  THEMED_SURFACE_COLORS as C,
  ThemedFixedTopBar,
  ThemedInput,
  ThemedPanel,
} from '../components/ui/ThemedSurface';

function fmtSum(raw: string): string {
  const n = Number(raw);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function FriendCard({
  friend,
  onIdClick,
}: {
  friend: FriendRow;
  onIdClick: (playerNumber: string) => void;
}) {
  const displayName = friend.nickname?.trim() || friend.username;
  const playerNumber = friend.player_number ?? String(friend.user_id);

  return (
    <ThemedPanel className="p-0">
      <div className="px-3 py-3">
        <div className="flex items-start gap-3">
          <div
            className="size-12 shrink-0 overflow-hidden rounded-full border"
            style={{ borderColor: `${C.gold}55`, background: C.chipBg }}
          >
            {friend.avatar_url ? (
              <img src={friend.avatar_url} alt="" className="size-full object-cover" draggable={false} />
            ) : (
              <div className="flex size-full items-center justify-center text-sm font-bold" style={{ color: C.gold }}>
                {(displayName[0] ?? '?').toUpperCase()}
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1 text-sm font-semibold" style={{ color: C.text }}>
              <span className="truncate">{displayName}</span>
              <button
                type="button"
                onClick={() => onIdClick(playerNumber)}
                className="shrink-0 underline decoration-dotted underline-offset-2"
                style={{ color: C.gold }}
              >
                [{playerNumber}]
              </button>
              <span className="text-xs" style={{ color: C.gold }}>
                {'★'.repeat(friend.star_count)}
              </span>
            </div>
            <div className="mt-1 text-xs" style={{ color: C.muted }}>
              {friend.masked_username}
            </div>
            <div className="mt-2 flex items-center gap-3 text-xs font-mono" style={{ color: C.text }}>
              <span>
                L: <span style={{ color: C.gold }}>{fmtSum(friend.left_sum)}</span>
              </span>
              <span>/</span>
              <span>
                R: <span style={{ color: C.gold }}>{fmtSum(friend.right_sum)}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </ThemedPanel>
  );
}

export function MyFriends() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');

  const friendsQuery = useQuery({
    queryKey: ['binary-tree', 'my-friends', query],
    queryFn: () => binaryTreeAPI.getMyFriends(query ? { search: query } : undefined),
    staleTime: 30_000,
  });

  const pendingQuery = useQuery({
    queryKey: ['binary-tree', 'pending-placements'],
    queryFn: () => binaryTreeAPI.getPendingPlacements(),
    staleTime: 30_000,
  });

  const friends = friendsQuery.data?.rows ?? [];
  const pending = pendingQuery.data?.rows ?? [];
  const total = friendsQuery.data?.total ?? 0;

  function onIdClick(playerNumber: string) {
    navigate(`/all-users?root=${encodeURIComponent(playerNumber)}`);
  }

  function runSearch() {
    setQuery(search.trim());
  }

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader
          title={t('myFriends.title', '我的好友')}
          onBack={() => navigate(-1)}
          theme={C}
          action={t('allUsers.title', '全球玩家')}
          onAction={() => navigate('/all-users')}
        />
        <ThemedFixedTopBar background={C.topbarBg}>
          <ThemedPanel className="p-4">
            <div className="flex gap-2">
              <ThemedInput
                type="text"
                placeholder={t('myFriends.searchPlaceholder', '搜索好友玩家ID')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && runSearch()}
              />
              <button
                type="button"
                onClick={runSearch}
                className="shrink-0 rounded-lg border px-4 text-sm font-semibold"
                style={{ borderColor: `${C.gold}66`, color: C.gold, background: C.chipBg }}
              >
                {t('common.search', '搜索')}
              </button>
            </div>
            <div className="mt-2 text-xs" style={{ color: C.muted }}>
              {t('myFriends.resultCount', '共 {{count}} 位好友', { count: total })}
            </div>
          </ThemedPanel>
        </ThemedFixedTopBar>

        <div className="flex flex-col gap-4 overflow-y-auto px-5 pt-[168px] pb-8">
          {friendsQuery.isLoading ? (
            <div className="flex flex-col gap-3">
              <LoadingSkeleton className="h-24" />
              <LoadingSkeleton className="h-24" />
            </div>
          ) : friends.length === 0 ? (
            <EmptyState title={t('myFriends.empty', '暂无好友')} />
          ) : (
            <div className="flex flex-col gap-3">
              {friends.map((f) => (
                <FriendCard key={f.user_id} friend={f} onIdClick={onIdClick} />
              ))}
            </div>
          )}

          <ThemedPanel className="p-0 overflow-hidden">
            <div className="border-b px-4 py-3 text-sm font-semibold" style={{ borderColor: C.line, color: C.text }}>
              {t('myFriends.pendingTitle', '待安置列表')}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] text-left text-[11px]">
                <thead>
                  <tr style={{ color: C.muted, background: C.inputBg }}>
                    <th className="px-2 py-2 font-medium">{t('myFriends.colSerial', '序号')}</th>
                    <th className="px-2 py-2 font-medium">{t('myFriends.colNickname', '昵称')}</th>
                    <th className="px-2 py-2 font-medium">{t('auth.username', '用户名')}</th>
                    <th className="px-2 py-2 font-medium">{t('myFriends.colStars', '玩家星级')}</th>
                    <th className="px-2 py-2 font-medium">{t('common.status', '状态')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pending.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-2 py-6 text-center" style={{ color: C.muted }}>
                        {t('common.noData', '暂无数据')}
                      </td>
                    </tr>
                  ) : (
                    pending.map((row, idx) => (
                      <tr key={row.user_id} className="border-t" style={{ borderColor: C.line, color: C.text }}>
                        <td className="px-2 py-2">{idx + 1}</td>
                        <td className="px-2 py-2">{row.nickname ?? '—'}</td>
                        <td className="px-2 py-2 font-mono">{row.username}</td>
                        <td className="px-2 py-2" style={{ color: C.gold }}>
                          {'★'.repeat(row.star_count)}
                        </td>
                        <td className="px-2 py-2">{row.status}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </ThemedPanel>
        </div>
      </div>
    </div>
  );
}
